import { io, Socket } from 'socket.io-client';

// Socket URL resolution:
// Use the proven API path for all traffic to ensure proxy compatibility.
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL
    || (import.meta.env.PROD ? window.location.origin : '');

const API_URL = import.meta.env.VITE_API_URL
    || (import.meta.env.PROD ? `${window.location.origin}/api/v1` : `http://${window.location.hostname}:3000/api/v1`);

class SocketService {
    private socket: Socket | null = null;
    private refreshingToken = false;

    connect(accessToken: string, namespace: '/staff' | '/admin' | '/customer'): Socket {
        if (this.socket) {
            this.socket.disconnect();
        }

        const fullUrl = `${SOCKET_URL}${namespace}`;
        console.log('[Socket] Connecting to:', fullUrl, 'token length:', accessToken?.length);

        this.socket = io(fullUrl, {
            path: '/api/v1/socket.io',
            auth: {
                token: accessToken,
            },
            // WebSocket only - do NOT add 'polling' back.
            // The backend runs several PM2 cluster processes behind one container.
            // A WebSocket is a single TCP connection that stays pinned to the
            // process that accepted it, so it needs no session affinity. Polling
            // needs every request of a session to reach the same process, and PM2
            // round-robins with no sticky-session support - Traefik cannot help
            // because it sees one container and has no say in the distribution
            // inside it. A client that falls back to polling therefore gets a
            // connection that breaks and reconnects forever.
            // A device that cannot open a WebSocket still works: the waiter list
            // refetches over REST every 20s (useWaiterOrders.ts).
            transports: ['websocket'],
            upgrade: false,
            timeout: 30000,
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 2000,
            reconnectionDelayMax: 10000,
        });

        this.socket.on('connect', () => {
            this.refreshingToken = false;
        });

        this.socket.on('connect_error', async (error: any) => {
            // Log full error details for debugging
            console.error('[Socket] connect_error:', {
                message: error.message,
                description: error.description,
                context: error.context,
                type: error.type,
                data: error.data,
                url: `${SOCKET_URL}${namespace}`,
            });

            if (error.message === 'server error' || error.message === 'Invalid or expired token') {
                // Token may be expired — fetch a fresh one before next reconnect
                if (!this.refreshingToken && this.socket) {
                    this.refreshingToken = true;
                    try {
                        const resp = await fetch(`${API_URL}/auth/socket-token`, {
                            credentials: 'include',
                        });
                        if (resp.ok) {
                            const { socketToken } = await resp.json();
                            if (this.socket) {
                                this.socket.auth = { token: socketToken };
                                console.debug('Socket token refreshed for reconnection');
                            }
                        } else {
                            console.error('[Socket] Token refresh failed:', resp.status, resp.statusText);
                        }
                    } catch (fetchErr) {
                        console.error('[Socket] Token refresh fetch error:', fetchErr);
                    } finally {
                        this.refreshingToken = false;
                    }
                }
            }
        });

        return this.socket;
    }

    disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    getSocket(): Socket | null {
        return this.socket;
    }
}

export const socketService = new SocketService();
